import React, { useState, useEffect } from 'react';
import { Plus, Search, Building2, Phone, Mail, Globe, Edit, Trash2, FileText, Star, Clock, DollarSign, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAYMENT_TERMS = [
  { id: 'cod', name: 'COD' },
  { id: 'net_15', name: 'Net 15' },
  { id: 'net_30', name: 'Net 30' },
  { id: 'net_45', name: 'Net 45' },
  { id: 'net_60', name: 'Net 60' },
];

const SUPPLIER_CATEGORIES = [
  { id: 'fabric', name: 'Fabrics' },
  { id: 'hardware', name: 'Hardware' },
  { id: 'motor', name: 'Motors' },
  { id: 'accessory', name: 'Accessories' },
];

const emptySupplier = {
  name: '',
  code: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  payment_terms: 'net_30',
  avg_lead_time_days: '',
  categories: [],
  notes: '',
};

const emptyPO = {
  supplier_id: '',
  supplier_name: '',
  items: [],
  tax: 0,
  shipping: 0,
  expected_date: '',
  notes: '',
};

export const SuppliersTab = ({ token }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('suppliers'); // suppliers, orders
  
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState(emptySupplier);
  
  const [showPOModal, setShowPOModal] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [poForm, setPOForm] = useState(emptyPO);
  
  const [showPODetailModal, setShowPODetailModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  useEffect(() => {
    fetchSuppliers();
    fetchPurchaseOrders();
    fetchInventory();
  }, []);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch POs:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    }
  };

  const saveSupplier = async () => {
    if (!supplierForm.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    try {
      if (editingSupplier) {
        await axios.put(`${API_URL}/admin/suppliers/${editingSupplier.id}`, supplierForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API_URL}/admin/suppliers`, supplierForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Supplier created');
      }
      setShowSupplierModal(false);
      setEditingSupplier(null);
      setSupplierForm(emptySupplier);
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to save supplier');
    }
  };

  const deleteSupplier = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await axios.delete(`${API_URL}/admin/suppliers/${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const savePurchaseOrder = async () => {
    if (!poForm.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (poForm.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const supplier = suppliers.find(s => s.id === poForm.supplier_id);
      const payload = {
        ...poForm,
        supplier_name: supplier?.name || '',
      };

      if (editingPO) {
        await axios.put(`${API_URL}/admin/purchase-orders/${editingPO.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order updated');
      } else {
        await axios.post(`${API_URL}/admin/purchase-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order created');
      }
      setShowPOModal(false);
      setEditingPO(null);
      setPOForm(emptyPO);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error('Failed to save purchase order');
    }
  };

  const receivePO = async (poId) => {
    try {
      const po = purchaseOrders.find(p => p.id === poId);
      const receivedItems = po.items.map(item => ({
        inventory_item_id: item.inventory_item_id,
        quantity_received: item.quantity_ordered
      }));

      await axios.post(`${API_URL}/admin/purchase-orders/${poId}/receive`, 
        { items: receivedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Purchase order received and inventory updated');
      fetchPurchaseOrders();
      fetchInventory();
    } catch (error) {
      toast.error('Failed to receive purchase order');
    }
  };

  const deletePO = async (poId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    try {
      await axios.delete(`${API_URL}/admin/purchase-orders/${poId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase order deleted');
      fetchPurchaseOrders();
    } catch (error) {
      toast.error('Failed to delete purchase order');
    }
  };

  const openEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name || '',
      code: supplier.code || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      website: supplier.website || '',
      address: supplier.address || '',
      city: supplier.city || '',
      state: supplier.state || '',
      zip_code: supplier.zip_code || '',
      payment_terms: supplier.payment_terms || 'net_30',
      avg_lead_time_days: supplier.avg_lead_time_days || '',
      categories: supplier.categories || [],
      notes: supplier.notes || '',
    });
    setShowSupplierModal(true);
  };

  const openCreatePO = (supplier = null) => {
    setEditingPO(null);
    setPOForm({
      ...emptyPO,
      supplier_id: supplier?.id || '',
      supplier_name: supplier?.name || '',
    });
    setShowPOModal(true);
  };

  const addItemToPO = () => {
    setPOForm(prev => ({
      ...prev,
      items: [...prev.items, {
        id: Date.now().toString(),
        inventory_item_id: '',
        sku: '',
        name: '',
        quantity_ordered: 1,
        unit: 'each',
        unit_cost: 0,
        total_cost: 0,
      }]
    }));
  };

  const updatePOItem = (itemId, field, value) => {
    setPOForm(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          if (field === 'inventory_item_id') {
            const invItem = inventory.find(i => i.id === value);
            if (invItem) {
              updated.sku = invItem.sku;
              updated.name = invItem.name;
              updated.unit = invItem.unit;
              updated.unit_cost = invItem.unit_cost;
              updated.total_cost = updated.quantity_ordered * invItem.unit_cost;
            }
          }
          if (field === 'quantity_ordered' || field === 'unit_cost') {
            updated.total_cost = updated.quantity_ordered * updated.unit_cost;
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const removeItemFromPO = (itemId) => {
    setPOForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getPOTotal = () => {
    const subtotal = poForm.items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    return subtotal + (poForm.tax || 0) + (poForm.shipping || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-neutral-100 text-neutral-600';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'partial': return 'bg-amber-100 text-amber-700';
      case 'received': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-neutral-100 text-neutral-600';
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPOs = purchaseOrders.filter(po =>
    po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    po.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="suppliers-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Suppliers & Purchase Orders</h2>
          <p className="text-neutral-500 text-sm">Manage vendors and procurement</p>
        </div>
        <div className="flex gap-2">
          {activeView === 'suppliers' ? (
            <Button onClick={() => { setEditingSupplier(null); setSupplierForm(emptySupplier); setShowSupplierModal(true); }} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> Add Supplier
            </Button>
          ) : (
            <Button onClick={() => openCreatePO()} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> Create PO
            </Button>
          )}
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-4 mb-6">
        <div className="flex bg-neutral-100 rounded-lg p-1">
          <button
            onClick={() => setActiveView('suppliers')}
            className={`px-4 py-2 text-sm rounded ${activeView === 'suppliers' ? 'bg-white shadow' : ''}`}
          >
            Suppliers ({suppliers.length})
          </button>
          <button
            onClick={() => setActiveView('orders')}
            className={`px-4 py-2 text-sm rounded ${activeView === 'orders' ? 'bg-white shadow' : ''}`}
          >
            Purchase Orders ({purchaseOrders.length})
          </button>
        </div>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder={activeView === 'suppliers' ? 'Search suppliers...' : 'Search POs...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : activeView === 'suppliers' ? (
        /* Suppliers List */
        filteredSuppliers.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg">
            <Building2 className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
            <p className="text-neutral-600">No suppliers found</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredSuppliers.map(supplier => (
              <div
                key={supplier.id}
                className="bg-white border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{supplier.name}</h4>
                    {supplier.code && (
                      <span className="text-xs text-neutral-400 font-mono">{supplier.code}</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openCreatePO(supplier)}
                      className="p-1.5 hover:bg-neutral-100 rounded"
                      title="Create PO"
                    >
                      <FileText className="w-4 h-4 text-neutral-500" />
                    </button>
                    <button
                      onClick={() => openEditSupplier(supplier)}
                      className="p-1.5 hover:bg-neutral-100 rounded"
                    >
                      <Edit className="w-4 h-4 text-neutral-500" />
                    </button>
                    <button
                      onClick={() => deleteSupplier(supplier.id)}
                      className="p-1.5 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5 text-sm mb-3">
                  {supplier.email && (
                    <p className="text-neutral-600 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-neutral-400" />
                      {supplier.email}
                    </p>
                  )}
                  {supplier.phone && (
                    <p className="text-neutral-600 flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-neutral-400" />
                      {supplier.phone}
                    </p>
                  )}
                  {supplier.website && (
                    <p className="text-neutral-600 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-neutral-400" />
                      {supplier.website}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {supplier.categories?.map(cat => (
                    <span key={cat} className="text-xs px-2 py-0.5 bg-neutral-100 rounded capitalize">
                      {cat}
                    </span>
                  ))}
                </div>

                <div className="pt-3 border-t border-neutral-100 flex items-center justify-between text-xs text-neutral-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {supplier.payment_terms?.replace('_', ' ').toUpperCase()}
                  </span>
                  {supplier.total_orders > 0 && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      {supplier.total_orders} orders
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* Purchase Orders List */
        filteredPOs.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg">
            <FileText className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
            <p className="text-neutral-600">No purchase orders found</p>
          </div>
        ) : (
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">PO #</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Supplier</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Date</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600">Items</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Total</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPOs.map(po => (
                  <tr key={po.id} className="border-b last:border-0 hover:bg-neutral-50">
                    <td className="px-4 py-3 font-mono text-sm font-medium text-amber-600">{po.po_number}</td>
                    <td className="px-4 py-3 text-sm">{po.supplier_name}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {new Date(po.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-center text-sm">{po.items?.length || 0}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(po.total)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusColor(po.status)}`}>
                        {po.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => { setSelectedPO(po); setShowPODetailModal(true); }}
                          className="p-1.5 hover:bg-neutral-100 rounded"
                          title="View details"
                        >
                          <ChevronRight className="w-4 h-4 text-neutral-500" />
                        </button>
                        {po.status !== 'received' && po.status !== 'cancelled' && (
                          <button
                            onClick={() => receivePO(po.id)}
                            className="p-1.5 hover:bg-green-50 rounded"
                            title="Mark as received"
                          >
                            <span className="text-green-600 text-xs font-medium">Receive</span>
                          </button>
                        )}
                        <button
                          onClick={() => deletePO(po.id)}
                          className="p-1.5 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Supplier Modal */}
      <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Code</Label>
                <Input
                  value={supplierForm.code}
                  onChange={(e) => setSupplierForm({ ...supplierForm, code: e.target.value })}
                  placeholder="Auto-generated"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={supplierForm.website}
                  onChange={(e) => setSupplierForm({ ...supplierForm, website: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={supplierForm.address}
                onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Input
                placeholder="City"
                value={supplierForm.city}
                onChange={(e) => setSupplierForm({ ...supplierForm, city: e.target.value })}
              />
              <Input
                placeholder="State"
                value={supplierForm.state}
                onChange={(e) => setSupplierForm({ ...supplierForm, state: e.target.value })}
              />
              <Input
                placeholder="ZIP"
                value={supplierForm.zip_code}
                onChange={(e) => setSupplierForm({ ...supplierForm, zip_code: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Terms</Label>
                <select
                  value={supplierForm.payment_terms}
                  onChange={(e) => setSupplierForm({ ...supplierForm, payment_terms: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  {PAYMENT_TERMS.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Avg Lead Time (days)</Label>
                <Input
                  type="number"
                  value={supplierForm.avg_lead_time_days}
                  onChange={(e) => setSupplierForm({ ...supplierForm, avg_lead_time_days: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Categories Supplied</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {SUPPLIER_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      const cats = supplierForm.categories.includes(cat.id)
                        ? supplierForm.categories.filter(c => c !== cat.id)
                        : [...supplierForm.categories, cat.id];
                      setSupplierForm({ ...supplierForm, categories: cats });
                    }}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      supplierForm.categories.includes(cat.id)
                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={supplierForm.notes}
                onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowSupplierModal(false)}>Cancel</Button>
            <Button onClick={saveSupplier} className="btn-primary">
              {editingSupplier ? 'Update' : 'Add'} Supplier
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create PO Modal */}
      <Dialog open={showPOModal} onOpenChange={setShowPOModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier *</Label>
                <select
                  value={poForm.supplier_id}
                  onChange={(e) => setPOForm({ ...poForm, supplier_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={poForm.expected_date}
                  onChange={(e) => setPOForm({ ...poForm, expected_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items</Label>
                <Button size="sm" variant="outline" onClick={addItemToPO}>
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              
              {poForm.items.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-4 bg-neutral-50 rounded-lg">
                  No items added yet
                </p>
              ) : (
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-center px-3 py-2 w-24">Qty</th>
                        <th className="text-right px-3 py-2 w-28">Unit Cost</th>
                        <th className="text-right px-3 py-2 w-28">Total</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {poForm.items.map(item => (
                        <tr key={item.id} className="border-t">
                          <td className="px-3 py-2">
                            <select
                              value={item.inventory_item_id}
                              onChange={(e) => updatePOItem(item.id, 'inventory_item_id', e.target.value)}
                              className="w-full px-2 py-1 border border-neutral-200 rounded text-sm"
                            >
                              <option value="">Select item...</option>
                              {inventory.map(inv => (
                                <option key={inv.id} value={inv.id}>
                                  {inv.name} ({inv.sku})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={item.quantity_ordered}
                              onChange={(e) => updatePOItem(item.id, 'quantity_ordered', parseFloat(e.target.value) || 0)}
                              className="text-center"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_cost}
                              onChange={(e) => updatePOItem(item.id, 'unit_cost', parseFloat(e.target.value) || 0)}
                              className="text-right"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {formatCurrency(item.total_cost)}
                          </td>
                          <td className="px-3 py-2">
                            <button onClick={() => removeItemFromPO(item.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Tax ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={poForm.tax}
                  onChange={(e) => setPOForm({ ...poForm, tax: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Shipping ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={poForm.shipping}
                  onChange={(e) => setPOForm({ ...poForm, shipping: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Total</Label>
                <div className="mt-1 px-3 py-2 bg-neutral-50 rounded-lg font-semibold text-lg">
                  {formatCurrency(getPOTotal())}
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={poForm.notes}
                onChange={(e) => setPOForm({ ...poForm, notes: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPOModal(false)}>Cancel</Button>
            <Button onClick={savePurchaseOrder} className="btn-primary">Create PO</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PO Detail Modal */}
      <Dialog open={showPODetailModal} onOpenChange={setShowPODetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="py-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{selectedPO.po_number}</h3>
                  <p className="text-neutral-500">{selectedPO.supplier_name}</p>
                </div>
                <span className={`px-3 py-1 rounded-lg text-sm capitalize ${getStatusColor(selectedPO.status)}`}>
                  {selectedPO.status}
                </span>
              </div>

              <div className="border border-neutral-200 rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-center px-3 py-2">Qty</th>
                      <th className="text-right px-3 py-2">Unit Cost</th>
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items?.map((item, idx) => (
                      <tr key={item.id || item.inventory_item_id || `po-item-${idx}`} className="border-t">
                        <td className="px-3 py-2">{item.name}</td>
                        <td className="px-3 py-2 text-center">{item.quantity_ordered} {item.unit}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(item.unit_cost)}</td>
                        <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-neutral-50 border-t">
                    <tr>
                      <td colSpan="3" className="px-3 py-2 text-right">Subtotal</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(selectedPO.subtotal)}</td>
                    </tr>
                    {selectedPO.tax > 0 && (
                      <tr>
                        <td colSpan="3" className="px-3 py-2 text-right">Tax</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(selectedPO.tax)}</td>
                      </tr>
                    )}
                    {selectedPO.shipping > 0 && (
                      <tr>
                        <td colSpan="3" className="px-3 py-2 text-right">Shipping</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(selectedPO.shipping)}</td>
                      </tr>
                    )}
                    <tr className="font-semibold">
                      <td colSpan="3" className="px-3 py-2 text-right">Total</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedPO.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedPO.notes && (
                <div className="bg-neutral-50 rounded-lg p-3">
                  <p className="text-sm text-neutral-600">{selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersTab;
